import { Button } from "@vds/buttons";
import { Icon } from "@vds/icons";
import { Box } from "@mui/material";
import React from "react";
import StackTrace from "stacktrace-js";
import NotificationBanner from "../../../layouts/NotificationBanner";
import { errorLogUpload, resetApp } from "../../../store/actions";
import { cancelTokenRefresh } from "../../proactiveTokenRefresh";
import { stopIdleTimer } from "../../idleTimeModal";
import { ConnectionRefusedError, DataError, NetworkError, ServerError } from "../../../utils/error";
import { TEXT } from "../../constants/constants";
import {
  ERROR_MESSAGES,
  ERROR_NETWORK_DISCONNECTED,
  ERROR_TITLES,
  ERROR_TYPES,
  ERROR_UNKNOWN,
  ERROR_WENT_WRONG,
  EXPECT_STRING
} from "../../constants/errorConstants";
import { clearAllCookies } from "../../utility/Utils";
import "./errorBoundary.css";
import { getRandomUUID } from "../../../Token";

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      showNotification: false,
      fullError: null,
      errorType: ERROR_TYPES.GENERIC,
      logId: null
    };
  }

  static getDerivedStateFromError(error) {
    let err;
    let errorType;
    let logId = null;
    let fullError = error;

    if (error instanceof ServerError) {
      errorType = ERROR_TYPES.SERVER;
      logId = error.logId; // Capture the X-Transaction-ID passed from the interceptor
      err = error.message;
    } else if (error instanceof ConnectionRefusedError) {
      errorType = ERROR_TYPES.NETWORK; // Still classified under NETWORK group
      logId = error.logId;
      err = error.message; // Contains neutral 'Service Unreachable' message
    } else if (error instanceof NetworkError) {
      errorType = ERROR_TYPES.NETWORK;
      logId = null; // True Network Error has no server-traceable ID
      err = error.message; // Contains 'Connection Lost' message
    } else if (error instanceof DataError) {
      errorType = ERROR_TYPES.DATA;
      logId = error.logId; // Capture the UUID generated within the DataError constructor
      err = error.message;
    } // --- Code and Generic Error Fallbacks ---
    else if (error instanceof TypeError || error instanceof ReferenceError) {
      errorType = ERROR_TYPES.CODE;
      logId = getRandomUUID(); // Generate new UUID for client-side bug tracing
      err = error.message;
    } else {
      // Final catch-all for unknown or uncaught exceptions
      errorType = ERROR_TYPES.GENERIC;
      logId = getRandomUUID(); // Generate new UUID for client-side bug tracing
      err = (error && typeof error.message === EXPECT_STRING && error.message) || ERROR_UNKNOWN;
    }

    return {
      hasError: true,
      error: err,
      fullError: fullError,
      errorType,
      logId
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.routeKey !== this.props.routeKey && this.state.hasError) {
      this.setState({ hasError: false });
    }
  }

  componentDidCatch(error, errorInfo) {
    // This is the temporary ID from getDerivedStateFromError (if applicable)
    const logId = this.state.logId;
    StackTrace.fromError(error).then((stackframes) => {
      const stringifiedStack = stackframes.map((sf) => sf.toString()).join("\n");
      // Inject the logId into the payload body
      const payloadWithErrorId = `--- TRACE_ID: ${logId} ---\n${stringifiedStack}`;
      console.log("Oops error full stack trace:", stringifiedStack);
      this.props.dispatch(
        errorLogUpload({
          error: payloadWithErrorId, // Payload includes the logId
          transactionId: logId
        })
      );
    });
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      showBanner: false,
      fullError: null,
      errorType: ERROR_TYPES.GENERIC,
      logId: null
    });
    this.props.navigate("/dashboard");
  };

  /**
   * Clears all local storage, cookies, and forces a hard reload.
   */
  handleClearCacheAndRetry = () => {
    console.log("Clearing application state and reloading...");

    // 1. STOP TIMERS: Prevent token refresh or idle modal from firing during reload.
    cancelTokenRefresh();
    stopIdleTimer();

    // 2. CLEAR REDUX STATE IN MEMORY: Dispatches action to reducers to return initialState.
    // This must be done first to prevent the store from trying to save old state.
    this.props.dispatch(resetApp());

    // 2. CLEAR PERSISTENT STORAGE: Clears data from the browser.
    clearAllCookies();
    sessionStorage.clear();
    localStorage.clear();

    // 3. FORCE RELOAD
    window.location.reload(true);
  };

  setShowNotification = (value) => {
    this.setState({ showNotification: value });
  };

  renderErrorPage = (title, message, extraMessage = null, showCacheButton = false) => {
    // Determine if the user is on the dashboard page
    const isOnDashboard = this.props.routeKey === "/dashboard" || this.props.routeKey === "/";

    // Set the text for the action button
    const retryButtonText = isOnDashboard ? TEXT.RELOAD_APPLICATION : TEXT.GO_TO_DASHBOARD;

    // Set the action for the button (Reload if on dashboard, Navigate otherwise)
    const retryButtonAction = isOnDashboard ? () => window.location.reload(true) : this.handleRetry; // handleRetry navigates to /dashboard
    return (
      <div className="error-boundary-container">
        <div className="error-boundary-content">
          <Icon name="error" className="error-boundary-icon" color="#FF8027" size={100} />
          <h1>{title}</h1>
          <p>{message}</p>
          {extraMessage && <p>{extraMessage}</p>}
          {/* LogID Display Logic: Show LogID if it is server-traceable or a code error that was logged */}
          {this.state.logId && !extraMessage && this.state.errorType !== ERROR_TYPES.NETWORK && (
            <p className="error-boundary-logid">Log ID: {this.state.logId}</p>
          )}
          <div className="error-boundary-actions">
            {/* Show the appropriate recovery button */}
            {!showCacheButton && <Button onClick={retryButtonAction}>{retryButtonText}</Button>}

            {showCacheButton && (
              <Box
                sx={{
                  display: "flex",
                  gap: 2
                }}
              >
                <Button onClick={this.handleClearCacheAndRetry}>{TEXT.CLEAR_CACHE_AND_RETRY}</Button>
                <Button kind="secondary" onClick={() => this.props.navigate(0)}>
                  {TEXT.RELOAD_APPLICATION}
                </Button>
              </Box>
            )}
          </div>
        </div>
      </div>
    );
  };

  render() {
    if (this.state.hasError) {
      switch (this.state.errorType) {
        case ERROR_TYPES.NETWORK:
          // The title and message are dynamically determined
          // by checking the specific message string stored in state.
          const isTrulyOffline = this.state.error.includes(ERROR_NETWORK_DISCONNECTED);

          const networkTitle = isTrulyOffline
            ? ERROR_TITLES.NETWORK_DISCONNECTED // "Connection Lost"
            : ERROR_TITLES.SERVICE_UNREACHABLE; // "Service Unreachable"

          // We display the specific error message stored in state.
          const networkMessage = this.state.error;

          return this.renderErrorPage(
            networkTitle,
            networkMessage,
            // Provide context for the user. If offline, prompt them to check connection.
            isTrulyOffline ? ERROR_MESSAGES.NETWORK_DISCONNECTED : ERROR_MESSAGES.SERVICE_UNREACHABLE,
            false // No cache button for network issues
          );
        case ERROR_TYPES.SERVER:
          return this.renderErrorPage(ERROR_TITLES.SERVER, ERROR_MESSAGES.SERVER);
        case ERROR_TYPES.DATA:
          return this.renderErrorPage(ERROR_TITLES.DATA, ERROR_MESSAGES.DATA);
        case ERROR_TYPES.CODE:
        case ERROR_TYPES.GENERIC:
        default:
          return this.renderErrorPage(
            ERROR_TITLES.GENERIC,
            ERROR_MESSAGES.GENERIC,
            ERROR_MESSAGES.GENERIC_EMAIL(this.state.logId),
            true // Show clear cache and retry button
          );
      }
    }
    if (this.props.errorData.message != null && this.props.errorData.errorType === ERROR_TYPES.ERROR) {
      return this.renderErrorPage(ERROR_MESSAGES.GENERIC, ERROR_MESSAGES.ERROR, null, true);
    } else if (this.props.errorData.message != null && this.props.errorData.errorType === ERROR_TYPES.WARNING) {
      return (
        <>
          <NotificationBanner
            key={this.props.errorData.notificationKey}
            notificationInfo={{
              type: ERROR_WENT_WRONG,
              priority: ERROR_TYPES.WARNING,
              title: this.props.errorData.message,
              showNotification: true
            }}
            setShowNotification={this.setShowNotification}
          />
          {this.props.children}
        </>
      );
    }
    return this.props.children;
  }
}
